'use client'

import { CornersOutIcon, XIcon } from '@phosphor-icons/react'
import { DialogClose } from '@radix-ui/react-dialog'
import clsx from 'clsx'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { ReactNode, useCallback, useEffect, useId, useState } from 'react'
import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import Button from '@/src/components/core/Button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '@/src/components/core/Dialog'
import Map from '@/src/components/elements/Map'
import Marker from '@/src/components/elements/Marker'
import Container from '@/src/components/layout/Container'

function pickupLabel(count: number): string {
  return count === 1 ? '1 Treffpunkt' : `${count} Treffpunkte`
}

const NumberedPin = ({ number }: { number: number }) => {
  const id = useId()
  return (
    <div className="relative h-10 w-8 shrink-0">
      <svg
        width="32"
        height="40"
        viewBox="0 0 24 30"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M12 0.5C15.0517 0.503707 17.9757 1.69584 20.1309 3.8125C22.2858 5.929 23.4963 8.7969 23.5 11.7861L23.4922 12.2402C23.3349 16.9186 20.7782 21.0602 18.1104 24.1299C15.3649 27.2888 12.5605 29.2456 12.3438 29.3945C12.2439 29.463 12.1237 29.5 12 29.5C11.8763 29.5 11.7561 29.463 11.6562 29.3945C11.4395 29.2456 8.63512 27.2888 5.88965 24.1299C3.13576 20.9613 0.500151 16.6505 0.5 11.7861L0.514648 11.2275C0.658484 8.44158 1.84877 5.7968 3.86914 3.8125C6.02433 1.69584 8.94834 0.503708 12 0.5Z"
          fill={`url(#${id}-gradient)`}
          stroke="white"
        />
        <defs>
          <linearGradient
            id={`${id}-gradient`}
            x1="12"
            y1="0"
            x2="12"
            y2="30"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#1A34D0" />
            <stop offset="1" stopColor="#1628A4" />
          </linearGradient>
        </defs>
      </svg>
      <span className="absolute top-2 left-1.75 flex h-[18px] w-[18px] items-center justify-center text-sm font-bold text-white">
        {number}
      </span>
    </div>
  )
}

const LocationMap = ({
  locations,
  selectedLocation,
}: FahrlehrerCh.TeacherProfile & {
  selectedLocation: FahrlehrerCh.TeacherProfileLocation
}) => {
  const rest = locations.filter((l) => l.id !== selectedLocation.id)
  const sorted = [
    selectedLocation,
    ...rest.filter((l) => l.hasBlinkLandingpage),
    ...rest.filter((l) => !l.hasBlinkLandingpage),
  ]
  const validLocations = sorted.filter((l) => l.coordinates !== null)
  if (validLocations.length === 0) return null

  return (
    <div className="mt-12 md:mt-24">
      <section id="locations">
        <Container>
          <div className="hidden md:block">
            <Desktop locations={validLocations} />
          </div>
          <div className="md:hidden">
            <Mobile locations={validLocations} />
          </div>
        </Container>
      </section>
    </div>
  )
}

const Desktop = ({
  locations,
}: {
  locations: FahrlehrerCh.TeacherProfileLocation[]
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  return (
    <div className="grid grid-cols-3 gap-12">
      <div>
        <h2 className="text-10xl font-black">Standorte</h2>
        <div className="max-h-108 space-y-2 overflow-y-auto pt-4">
          {locations.map((location, index) => (
            <button
              key={location.id}
              className="flex w-full items-center gap-3 rounded-lg p-2 text-left hover:bg-gray-50"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <NumberedPin number={index + 1} />
              <div>
                <p className="font-bold">{location.name}</p>
                <p className="text-sm text-gray-600">
                  {pickupLabel(
                    location.addresses.filter((a) => a.type === 'pickup').length
                  )}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
      <ExpandableMap locations={locations}>
        <div className="relative col-span-2 h-120 overflow-hidden rounded-xl border border-gray-100">
          <Map
            markers={locations.map((l) => [
              l.coordinates!.lng,
              l.coordinates!.lat,
            ])}
            interactive={false}
            className="size-full"
          >
            {locations.map((location, index) => (
              <Marker
                key={location.id}
                position={{
                  lat: location.coordinates!.lat,
                  lng: location.coordinates!.lng,
                }}
                icon="teacher"
                number={index + 1}
                focused={hoveredIndex === index}
                size="md"
                zIndex={locations.length - index}
              />
            ))}
          </Map>
          <div className="absolute top-2 right-2">
            <DialogTrigger>
              <Button
                as="div"
                version="white-gray"
                size="icon-small"
                icon={<CornersOutIcon weight="bold" />}
              />
            </DialogTrigger>
          </div>
        </div>
      </ExpandableMap>
    </div>
  )
}

const Mobile = ({
  locations,
}: {
  locations: FahrlehrerCh.TeacherProfileLocation[]
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false })

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  return (
    <>
      <h2 className="text-4xl font-black">Standorte</h2>
      <ExpandableMap locations={locations}>
        <div className="relative mt-5 h-56 w-full overflow-hidden rounded-xl border border-gray-100">
          <Map
            markers={locations.map((l) => [
              l.coordinates!.lng,
              l.coordinates!.lat,
            ])}
            interactive={false}
            className="size-full"
          >
            {locations.map((location, index) => (
              <Marker
                key={location.id}
                position={{
                  lat: location.coordinates!.lat,
                  lng: location.coordinates!.lng,
                }}
                icon="teacher"
                number={index + 1}
                focused={index === selectedIndex}
                zIndex={locations.length - index}
              />
            ))}
          </Map>
          <div className="absolute top-2 right-2">
            <DialogTrigger>
              <Button
                as="div"
                version="white-gray"
                size="icon-small"
                icon={<CornersOutIcon weight="bold" />}
              />
            </DialogTrigger>
          </div>
        </div>
      </ExpandableMap>
      <div ref={emblaRef} className="-mx-5 mt-6 overflow-hidden">
        <ul className="flex select-none">
          {locations.map((location, index) => (
            <li
              key={location.id}
              className="ml-5 min-w-0 flex-[0_0_calc(100%-17px-40px)] last:mr-5"
            >
              <MobileLocationCard location={location} index={index} />
            </li>
          ))}
        </ul>
      </div>
      {locations.length > 1 && (
        <div className="mt-4 flex justify-center space-x-1">
          {locations.map((_, index) => (
            <div
              key={index}
              className={clsx('rounded-full transition-all', {
                'size-2 bg-gray-200': selectedIndex !== index,
                'h-2 w-3 bg-gray-800': selectedIndex === index,
              })}
            />
          ))}
        </div>
      )}
    </>
  )
}

const MobileLocationCard = ({
  location,
  index,
}: {
  location: FahrlehrerCh.TeacherProfileLocation
  index: number
}) => (
  <div className="flex items-center gap-3 rounded-lg bg-gray-50 px-4 py-3">
    <NumberedPin number={index + 1} />
    <div>
      <p className="font-bold">{location.name}</p>
      <p className="text-sm text-gray-600">
        {pickupLabel(
          location.addresses.filter((a) => a.type === 'pickup').length
        )}
      </p>
    </div>
  </div>
)

const ExpandableMap = ({
  children,
  locations,
}: {
  children: ReactNode
  locations: FahrlehrerCh.TeacherProfileLocation[]
}) => {
  const pickupAddresses = locations.flatMap((l) =>
    l.addresses.filter(
      (a) => a.type === 'pickup' && a.latitude !== null && a.longitude !== null
    )
  )

  return (
    <Dialog>
      {children}
      <DialogContent
        withPositioning={false}
        mapSafe
        className="inset-x-0 top-21 bottom-0 w-auto overflow-hidden rounded-t-xl md:inset-x-10 md:top-9.5 md:bottom-10 md:rounded-2xl"
      >
        <DialogHeader title="Alle Treffpunkte" className="md:hidden" />
        <div className="relative size-full">
          <Map
            markers={pickupAddresses.map((a) => [a.longitude!, a.latitude!])}
            zoomAfterClick={false}
            className="size-full"
          >
            {pickupAddresses.map((address, index) => (
              <Marker
                key={index}
                position={{ lng: address.longitude!, lat: address.latitude! }}
                icon="pickup"
                size="lg"
                zIndex={pickupAddresses.length - index}
              >
                {`
                      <div>
                        <h4 class="leading-5 font-black text-base">${address.name}</h4>
                        <a class="mt-1 block text-blue-700 text-sm" href="https://www.google.com/maps/place/${
                          address.place
                        }+${address.street.replace(
                          ' ',
                          '+'
                        )}" target="_blank" rel="noopener noreferrer">
                          ${address.street}, ${address.place}
                        </a>
                      </div>
                    `}
              </Marker>
            ))}
          </Map>
          <div className="pointer-events-none absolute inset-x-0 top-0 hidden h-20 bg-linear-to-b from-black/50 to-black/0 md:block" />
          <div className="absolute inset-x-0 top-0 hidden items-center justify-between px-6 pt-6 md:flex">
            <p className="text-4xl font-black text-white text-shadow-md">
              Alle Treffpunkte
            </p>
            <DialogClose asChild>
              <Button
                version="white-gray"
                size="icon-small"
                icon={<XIcon weight="bold" />}
              />
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default LocationMap
